import { useEffect, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import changeRatio from '../../../helpers/changeRatio';
import withComponentName from '../../../decorators/withComponentName';

import Styles from './styles.module.scss';

export function TrendChart(props) {
  const {
    className,
    width,
    height,
    incrementColor,
    decrementColor,
    neutralColor,
    data: inputData,
    skipGap,
    hasBigScreenDesign,
    isSeo,
    loading,
    lineWidth,
    expanded,
    chatbot,
    ...otherProps
  } = props;
  let data = inputData;
  const canvasRef = useRef(null);

  const isBigScreen = useSelector(({ common }) => (
    (common.width >= 2800)
  ));

  useEffect(() => {
    const canvasElement = canvasRef.current;
    let { width, height } = canvasElement;
    const ctx = canvasElement.getContext('2d');
    width -= 2; height -= 2;

    if (data && data.length > 1) {
      data = (
        data
          .map((v, i) => {
            let xData;
            if (!skipGap) {
              xData = v.xData instanceof Date ? v.xData.getTime() : v.xData;
            } else {
              xData = i;
            }

            return {
              ...v,
              xData,
              YData: v.yData instanceof Date ? v.yData.getTime() : v.yData,
            };
          })
          .sort((a, b) => a.xData - b.xData)
      );

      const minX = Math.min(...data.map((v) => v.xData));
      const minY = Math.min(...data.map((v) => v.yData));
      data = data.map((v) => ({
        ...v,
        xData: v.xData - minX,
        yData: v.yData - minY,
      }));

      const { xData: xLimit } = data[data.length - 1];
      let yLimit = Math.max(...data.map((v) => v.yData));

      if (data.every((v) => !v.yData)) {
        yLimit = height;
        data = data.map((v) => ({
          ...v,
          yData: Math.floor(height / 2),
        }));
      }

      if (isSeo) {
        ctx.lineWidth = hasBigScreenDesign && isBigScreen ? 2 : 1;
      } else {
        ctx.lineWidth = hasBigScreenDesign && isBigScreen ? 4 : 1;
      }

      if (lineWidth) {
        ctx.lineWidth = lineWidth;
      }

      let chartType;
      const firstPoint = data[0].yData;
      const lastPoint = data[data.length - 1].yData;
      if (lastPoint > firstPoint) {
        chartType = incrementColor;
      } else if (firstPoint === lastPoint) {
        chartType = neutralColor;
      } else {
        chartType = decrementColor;
      }
      if (loading) chartType = neutralColor;

      data.slice(1).forEach((value, i) => {
        const prevValue = data[i];

        ctx.beginPath();
        ctx.strokeStyle = chartType;
        ctx.moveTo(
          changeRatio(xLimit, width, prevValue.xData) || 1,
          changeRatio(yLimit, height, prevValue.yData) || 1,
        );
        ctx.lineTo(
          changeRatio(xLimit, width, value.xData) || 1,
          changeRatio(yLimit, height, value.yData) || 1,
        );
        ctx.stroke();
      });
    }

    return () => ctx.clearRect(0, 0, canvasElement.width, canvasElement.height);
  }, [
    data, skipGap, incrementColor, decrementColor,
    neutralColor, isBigScreen, loading, lineWidth,
    expanded, chatbot, width, height,
  ]);

  return (
    <section
      className={`${Styles['trend-chart']} ${className}`}
      {...otherProps}
    >
      <canvas
        width={width}
        height={height}
        ref={canvasRef}
      />
    </section>
  );
}

TrendChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      xData: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.instanceOf(Date),
      ]),
      yData: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.instanceOf(Date),
      ]),
    }),
  ),
  incrementColor: PropTypes.string,
  decrementColor: PropTypes.string,
  neutralColor: PropTypes.string,
  className: PropTypes.string,
  width: PropTypes.string,
  height: PropTypes.string,
  skipGap: PropTypes.bool,
  lineWidth: PropTypes.number,
};

TrendChart.defaultProps = {
  data: [],
  incrementColor: '#52b564',
  decrementColor: '#e25656',
  neutralColor: '#666666',
  className: '',
  width: '320',
  height: '200',
  skipGap: false,
  lineWidth: null,
};

export default withComponentName(memo(TrendChart));
